import useTranslation from 'next-translate/useTranslation'
import React, { Dispatch, FC, MutableRefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  toaster,
  Notification,
  FlexboxGrid,
} from 'rsuite'
import { useRouter } from 'next/router'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import usePrice from '../../services/usePrice'
import {
  getTotalProductQuantity,
  getProductVariantQuantities,
  getIsOverExactStock,
  hasStockBackDate,
  getOrderedProductCategories,
  getDeliveryEstimation,
  getProductCurrency,
} from '../../lib/product'
import { ALERT_DURATION, ScreenSize } from '../../utils/constants'
import { formatPriceNumber } from '../../utils/math'
import { Product, ProductQuantity, Supplier } from '../../utils/types/Product'
import orderRequestManualCheckLimitEvent from '../../utils/analytics/orderRequestManualCheckLimitEvent'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import ProductVariants, { ProductVariantsRef } from '../ProductQuantitySection'
import useRFQModal from '../Modals/hooks/useRFQModal'
import useIsOnMobile from '../../services/useIsOnMobile'
import { isElementVisible } from '../../utils/scroll'
import { useCart } from '../Cart/CartProvider'
import { useAuth } from '../../services/useAuth'
import { getSupplierMovData } from '../../lib/cart'
import { isSuperUser } from '../../lib/supplier'
import useEcomEvents from '../../services/analytics/useEcomEvents'
import { isOrderPriceLimitReached } from '../../lib/resources/price'
import ProductPriceSection from './ProductPriceSection'
import ProductDiscountBar from './ProductDiscountBar'
import ProductShippingInfo from './ProductShippingInfo'
import PackagingDetailsSPP from './PackagingDetailsSPP'
import ContactCard from '../ContactCard'
import DiscountTableSPP from './DiscountTableSPP'
import AddProductButtonsSPP from './AddProductButtonsSPP'
import { useCyModelWithProps } from '../../services/useCyModel'
import useRecentlyViewedProducts from '../../services/useRecentlyViewedProducts'
import ProductCarousel from '../Carousels/ProductCarousel'
import { AnalyticsProductCarouselTypes } from '../../utils/types/analytics'
import useOthersYouMayLike from '../../services/recommendationsAPI/useOthersYouMayLike'
import ShippingIconBorderless from '../Icons/ShippingIconBorderless'
import { getSupplierDeliveryDetails } from '../../lib/service'
import MobileCartModal from './MobileCartModal'

import buttonStyles from '../../styles/CustomButtons.module.less'
import styles from '../../styles/ProductColumn.module.less'

interface ProductColumnProps {
  product: Product
  supplier?: Supplier
  productQuantity: ProductQuantity[]
  toggleOrderModal: (val: boolean) => void
  setProductQuantity: Dispatch<SetStateAction<ProductQuantity[]>>
  addToCartLink: string
  className?: string
  isFavoriteProduct?: boolean
  isNewProductAdded: MutableRefObject<boolean>
}

const ProductColumn: FC<ProductColumnProps> = (props) => {
  const {
    product,
    supplier,
    productQuantity,
    addToCartLink,
    className,
    setProductQuantity,
    toggleOrderModal,
    isNewProductAdded,
  } = props

  const [isLoading, setIsLoading] = useState(false)
  // Triggered when product added to cart and MOQ is not met
  const [isMoqWarningActive, setIsMoqWarningActive] = useState(false)

  const { addProduct, refresh } = useCart()
  const { locale: localeAndCountryString, isReady: isRouterReady } = useRouter()
  const { locale, country } = getCountryAndLocaleStrings(localeAndCountryString)
  const { user, isVatIncluded } = useAuth()

  // Feature flags
  const deliveryDetailsV3 = useFeatureIsOn('spp-delivery-details-v3')

  const {
    data: othersYouMayLike,
    isLoading: isOthersYouMayLikeLoading,
    triggerDetailPageView,
  } = useOthersYouMayLike({ pageSize: 20 })

  const {
    data: recentlyViewedProducts,
    isLoading: isRecentlyViewedProductsLoading,
  } = useRecentlyViewedProducts()

  // Remove current SPP from recently viewed products
  const recentlyViewedProductsFiltered = recentlyViewedProducts?.filter((prod) => prod['product.code'] !== product['product.code'])

  const totalQuantity = getTotalProductQuantity(productQuantity)
  const {
    default: {
      price: defaultPrice,
    },
    final: {
      price: finalProductPrice,
      total: finalTotalPrice,
    },
    discount: {
      current: currentDiscount,
      upcoming: upcomingDiscount,
    },
    moq,
  } = usePrice(product, totalQuantity)

  const { t } = useTranslation('products')
  const isOnMobile = useIsOnMobile(ScreenSize.md)

  const productVariantsRef = useRef<ProductVariantsRef>(null)
  const addToCartWrapperRef = useRef<HTMLDivElement>(null)

  const analyticsLocation = 'single_product'
  const productVariants = getProductVariantQuantities(productQuantity)
  const orderedCategories = getOrderedProductCategories(product)
  const currency = getProductCurrency(product)

  const showChooseQuantityHeader = () => {
    const chooseQuantityElementRef = productVariantsRef.current?.chooseQuantityRef
    const isChooseQuantityVisible = isElementVisible(chooseQuantityElementRef?.current)

    if (!isChooseQuantityVisible) {
      productVariantsRef.current?.chooseQuantityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const openRFQModal = useRFQModal({
    analyticsLocation,
    product,
  })

  const productPrice = formatPriceNumber(finalProductPrice)
  const isOverExactStock = productQuantity.every(
    (ele) => getIsOverExactStock(ele)
    || (ele.exactStockLevel !== null && Number(ele.exactStockLevel) <= 0),
  )

  const { addToCartEvent, addToCartUnderMOQEvent } = useEcomEvents()

  // Allowing super user to add product to cart even if order amount greater than max price limit
  const isProductOrderLimitReached = !isSuperUser(user) && isOrderPriceLimitReached(
    finalTotalPrice, product,
  )

  const hasAddedQuantityWithoutSbd = productQuantity
    .some((ele) => ele.exactStockLevel !== null
    && Number(ele.quantity) > Number(ele.exactStockLevel)
    && !hasStockBackDate(ele.stockDate))

  const handleAddToCart = async () => {
    if (!supplier) { return } // Don't do anything until supplier data has loaded

    const isUnderMoq = moq > Number(totalQuantity)
      || Number.isNaN(+totalQuantity)
      || Number.isNaN(productPrice)

    // Faulty add to cart – product amount is below MOQ
    if (isUnderMoq) {
      showChooseQuantityHeader()

      // Analytics event
      if (!isMoqWarningActive) {
        addToCartUnderMOQEvent({ productsData: [{ product, productVariants }] })
      }
      setIsMoqWarningActive(true)
      return
    }

    const cartData = productQuantity.map((prodQuantity) => ({
      id: prodQuantity.productId,
      quantity: Number(prodQuantity.quantity),
      supplierId: supplier.id,
      variant: prodQuantity.variantId,
    }))

    setIsLoading(true)
    isNewProductAdded.current = true

    const { error } = await addProduct(addToCartLink, cartData)

    setIsLoading(false)

    if (error) {
      toaster.push((
        <Notification
          type="info"
          header={t('common:Oh no!')}
          duration={ALERT_DURATION}
          closable
        >
          <p>{`${t('cart:Something went wrong, unable to add product to cart')}.`}</p>
        </Notification>
      ),
      { placement: 'topEnd' })
    } else {
      setIsMoqWarningActive(false)

      // Analytics event
      // Get augmented cart with product and supplier data for correct isUnderMov value
      const { modifiedCart } = (await refresh([supplier], [product])) || {}
      const {
        isUnderMov,
      } = getSupplierMovData(supplier['supplier.code'], country, modifiedCart, isVatIncluded)

      // Reset selected quantity after add to cart
      setProductQuantity((prevState) => prevState.map((quantity) => ({
        ...quantity,
        quantity: 0,
      })))
      addToCartEvent({ modifiedCart, productsData: [{ product, productVariants, isUnderMov }] })
    }
  }

  const isRFQProduct = !defaultPrice

  useEffect(() => {
    triggerDetailPageView([product['product.code']])
  }, [product['product.code']])

  const onAddToCartClick = useCallback(
    !isProductOrderLimitReached && !isOverExactStock
      ? handleAddToCart
      : () => {
      // Analytics event
        orderRequestManualCheckLimitEvent(product, totalQuantity)
        toggleOrderModal(true)
      },
    [
      isProductOrderLimitReached,
      isOverExactStock,
      hasAddedQuantityWithoutSbd,
      handleAddToCart,
    ],
  )

  useCyModelWithProps('SingleProduct', {
    openRFQModal,
  })

  const {
    freeShippingLimit,
    deliveryCost,
  } = getSupplierDeliveryDetails(supplier, country)

  const deliveryDetailsDeliveryDateV3 = deliveryDetailsV3
    ? (
      <FlexboxGrid className={`flex-flow-row ${styles['estimated-delivery-date']}`}>
        <ShippingIconBorderless />
        {`${t('products:Estimated delivery')}: ${getDeliveryEstimation(product, country, locale)}` }
      </FlexboxGrid>
    )
    : null

  const deliveryDetailsDeliveryFeeV3 = deliveryDetailsV3
    ? (
      <p className="margin-top-spacer-double">
        {t('products:Shipping fee is {{ shippingFee }} {{ currency }} for orders under {{ amount }} {{ currency }}',
          { shippingFee: formatPriceNumber(deliveryCost),
            currency,
            amount: formatPriceNumber(freeShippingLimit) })}
      </p>
    )
    : null

  return (
    <div className={`${className} ${styles['product-column-right']}`}>
      {isOnMobile
        ? (
          <>
            <AddProductButtonsSPP
              isRFQProduct={isRFQProduct}
              isLoading={isLoading}
              finalTotalPrice={finalTotalPrice}
              product={product}
              isOverExactStock={isOverExactStock}
              onAddToCartClick={onAddToCartClick}
              className={styles['add-to-cart-mobile-sticky']}
            />

            {!isRFQProduct && (
              <>
                <div className={styles['product-info']}>
                  <ProductPriceSection
                    product={product}
                    productQuantity={productQuantity}
                  />
                </div>
                <div className="margin-top-spacer-double">
                  <ProductDiscountBar
                    product={product}
                    productQuantity={productQuantity}
                  />
                </div>
              </>
            )}

            {!deliveryDetailsV3 && (
              <ProductShippingInfo
                supplier={supplier}
                product={product}
                productQuantity={productQuantity}
              />
            )}

            {deliveryDetailsDeliveryDateV3}

            <div className={styles['product-selection-section']}>
              <ProductVariants
                ref={productVariantsRef}
                product={product}
                productQuantity={productQuantity}
                setProductQuantity={setProductQuantity}
                upcomingDiscount={upcomingDiscount}
                currentDiscount={currentDiscount}
                isMoqWarningActive={isMoqWarningActive}
                setIsMoqWarningActive={setIsMoqWarningActive}
              />
            </div>

            {deliveryDetailsDeliveryFeeV3}

            <PackagingDetailsSPP product={product} />

            <FlexboxGrid
              className="margin-top-spacer-tripple"
              align="middle"
            >
              <FlexboxGrid.Item>
                <p className={styles['rfq-text']}>
                  {t('Need larger quantities')}
                </p>
              </FlexboxGrid.Item>

              <Button
                data-testid="request-quote-button"
                disabled={!isRouterReady}
                onClick={() => openRFQModal()}
                className={`
                            padding-zero
                            ${buttonStyles['custom-button']}
                            ${buttonStyles['button-tertiary']}
                            ${buttonStyles['button-transparent']}
                            ${buttonStyles['button-caret']}
                            `}
                type="button"
              >
                <b className={styles['rfq-btn']}>{t('Request a custom quote')}</b>
              </Button>
            </FlexboxGrid>

            {!isRFQProduct && <DiscountTableSPP product={product} />}

            <ProductCarousel
              carouselType={AnalyticsProductCarouselTypes.OthersYouMayLike}
              carouselGap={0} // Gap 0 for mobile for more compact layout
              productCardType="simple"
              title={t('products:Other products you may like')}
              titleSpacerClassName="margin-bottom-spacer"
              products={othersYouMayLike}
              isLoading={isOthersYouMayLikeLoading}
              className="margin-top-spacer-tripple"
              hasCardBorder={false}
              hideArrows={!!(othersYouMayLike?.length && othersYouMayLike.length < 3)}
            />
            <ProductCarousel
              carouselType={AnalyticsProductCarouselTypes.RecentlyViewed}
              carouselGap={0} // Gap 0 for mobile for more compact layout
              productCardType="simple"
              className="margin-top-spacer-tripple"
              title={t('products:Recently viewed')}
              titleSpacerClassName="margin-bottom-spacer"
              products={recentlyViewedProductsFiltered}
              hasCardBorder={false}
              hideArrows={!!(
                recentlyViewedProducts?.length && recentlyViewedProducts.length < 3
              )}
              hide={recentlyViewedProductsFiltered?.length === 0
                        && !isRecentlyViewedProductsLoading}
            />
            <div
              className={`${styles['product-contact-card']}`}
              data-testid="product-contact-card"
            >
              <ContactCard
                hasPhone
                product={product}
                categories={orderedCategories}
                analyticsContactContext="product_specialist_single_product"
                title={t('modals:Need help?')}
              />
            </div>
          </>
        )
        : (
          <>

            <div className="padding-right-spacer-double padding-left-spacer-double">
              {!isRFQProduct && (
                <>
                  <div className={styles['product-info']}>
                    <ProductPriceSection
                      product={product}
                      productQuantity={productQuantity}
                    />
                  </div>
                  <div className="margin-top-spacer-double">
                    <ProductDiscountBar
                      product={product}
                      productQuantity={productQuantity}
                    />
                  </div>
                </>
              )}

              {!deliveryDetailsV3 && (
                <ProductShippingInfo
                  supplier={supplier}
                  product={product}
                  productQuantity={productQuantity}
                />
              )}

              {deliveryDetailsDeliveryDateV3}
            </div>

            <div className={styles['product-selection-section']}>
              <div className="margin-bottom-spacer-double">
                <ProductVariants
                  ref={productVariantsRef}
                  product={product}
                  productQuantity={productQuantity}
                  setProductQuantity={setProductQuantity}
                  upcomingDiscount={upcomingDiscount}
                  currentDiscount={currentDiscount}
                  isMoqWarningActive={isMoqWarningActive}
                  setIsMoqWarningActive={setIsMoqWarningActive}
                />
              </div>
              <div
                ref={addToCartWrapperRef}
                data-testid="checkout-section-wrapper"
                className={styles['checkout-section-wrapper']}
              >
                <AddProductButtonsSPP
                  isRFQProduct={isRFQProduct}
                  isLoading={isLoading}
                  finalTotalPrice={finalTotalPrice}
                  isOverExactStock={isOverExactStock}
                  product={product}
                  onAddToCartClick={onAddToCartClick}
                />

                {deliveryDetailsDeliveryFeeV3}
              </div>

              <PackagingDetailsSPP product={product} />

              <FlexboxGrid
                className="margin-top-spacer-tripple"
                align="middle"
              >
                <FlexboxGrid.Item>
                  <p className={styles['rfq-text']}>
                    {t('Need larger quantities')}
                  </p>
                </FlexboxGrid.Item>
                <Button
                  data-testid="request-quote-button"
                  disabled={!isRouterReady}
                  onClick={() => openRFQModal()}
                  className={`
                            padding-zero
                            ${buttonStyles['custom-button']}
                            ${buttonStyles['button-tertiary']}
                            ${buttonStyles['button-transparent']}
                            ${buttonStyles['button-caret']}
                            `}
                  type="button"
                >
                  <b className={styles['rfq-btn']}>{t('Request a custom quote')}</b>
                </Button>
              </FlexboxGrid>

              {!isRFQProduct && <DiscountTableSPP product={product} />}

              <ProductCarousel
                carouselType={AnalyticsProductCarouselTypes.OthersYouMayLike}
                productCardType="simple"
                title={t('products:Other products you may like')}
                titleSpacerClassName="margin-bottom-spacer"
                products={othersYouMayLike}
                isLoading={isOthersYouMayLikeLoading}
                className="margin-top-spacer-tripple"
                hasCardBorder={false}
                hideArrows={!!(othersYouMayLike?.length && othersYouMayLike.length < 3)}
              />
              <ProductCarousel
                carouselType={AnalyticsProductCarouselTypes.RecentlyViewed}
                productCardType="simple"
                className="margin-top-spacer-tripple"
                title={t('products:Recently viewed')}
                titleSpacerClassName="margin-bottom-spacer"
                products={recentlyViewedProductsFiltered}
                hasCardBorder={false}
                hideArrows={!!(
                  recentlyViewedProducts?.length && recentlyViewedProducts.length < 3
                )}
                hide={
                  !recentlyViewedProductsFiltered?.length && !isRecentlyViewedProductsLoading
                  }
              />
              <div
                className={`${styles['product-contact-card']}`}
                data-testid="product-contact-card"
              >
                <ContactCard
                  hasPhone
                  product={product}
                  categories={orderedCategories}
                  analyticsContactContext="product_specialist_single_product"
                  title={t('modals:Need help?')}
                />
              </div>
            </div>
          </>
        )}
      <MobileCartModal />
    </div>
  )
}

export default ProductColumn
